import { Link, useNavigate, useRevalidator } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { basketService } from "@/services/basket/basketService.ts";
import { authService } from "@/services/auth/authService.ts";
import { useTranslation } from "react-i18next";
import {
  CourseDetailCategory,
  CourseDetailTag,
} from "@/routes/course/types/courseDetail.ts";

type CourseSidebarProps = {
  courseId: string;
  isOwned: boolean;
  isInTheBasket: boolean;
  totalLessons: number;
  courseProgress: number | null;
  enrolledStudents: number;
  image: string;
  isCertified: boolean;
  isAuthenticated: boolean;
  price: number;
  priceText: string;
  discountedPrice: number;
  discountedPriceText: string;
  tags: CourseDetailTag[] | null | undefined;
  categories: CourseDetailCategory[] | null | undefined;
};
const CourseSidebar = ({
  isOwned,
  courseId,
  isInTheBasket,
  totalLessons,
  courseProgress,
  enrolledStudents,
  image,
  isAuthenticated,
  price,
  priceText,
  discountedPrice,
  discountedPriceText,
  tags,
  categories,
}: CourseSidebarProps) => {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const navigate = useNavigate();

  const percentageCompleted = courseProgress ?? 0;
  const completedLessons =
    courseProgress !== null
      ? Math.round((courseProgress / 100) * totalLessons)
      : 0;

  const handleAddToBasket = async (courseId: string) => {
    if (!authService.getAccessToken()) {
      navigate("/login");
      return;
    }
    await basketService.addItemToBasket({
      courseId: courseId,
      basketId: null,
    });
    revalidator.revalidate();
  };

  const categoryNames = (categories ?? []).map((c) => c.name).filter(Boolean);
  const tagNames = (tags ?? []).map((t) => t.name).filter(Boolean);
  const hasTagsOrCategories = categoryNames.length > 0 || tagNames.length > 0;

  return (
    <div className="h-full p-6">
      <div className="space-y-6">
        {/* Course Image */}
        <div className="w-full max-w-sm mx-auto">
          <div className="aspect-video bg-muted rounded-lg mb-4 flex items-center justify-center">
            <img
              src={image || "/placeholder.svg?height=200&width=400"}
              alt="course-image"
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        </div>

        {/* Price */}
        {!isOwned && (
          <div className="flex items-center gap-2 flex-wrap">
            {price !== discountedPrice ? (
              <>
                <span className="text-2xl font-bold text-orange-500">
                  {discountedPriceText}
                </span>
                <span className="text-base font-medium line-through text-gray-400">
                  {priceText}
                </span>
                <Badge className="bg-orange-500 hover:bg-orange-500 text-white text-xs px-2 py-0.5">
                  %{Math.round((1 - discountedPrice / price) * 100)} {t("course.off")}
                </Badge>
              </>
            ) : (
              <span className="text-2xl font-bold">{priceText}</span>
            )}
          </div>
        )}

        {isOwned ? (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{t("course.progress")}</span>
              <span>
                {completedLessons}/{totalLessons} {t("course.lessons")}
              </span>
            </div>
            <Progress value={percentageCompleted} className="h-2" />
            <div className="text-right text-sm text-muted-foreground pb-1">
              {Math.round(percentageCompleted)}% {t("common.completed")}
            </div>
            <Link to={"learning"}>
              <Button className="w-full">{t("course.continueLearning")}</Button>
            </Link>
          </div>
        ) : isInTheBasket ? (
          <Link to={"/basket/"}>
            <Button className="w-full">{t("common.goToBasket")}</Button>
          </Link>
        ) : (
          <Button
            className="w-full"
            onClick={(e) => {
              e.preventDefault();
              handleAddToBasket(courseId);
            }}
          >
            {isAuthenticated ? t("common.addToBasket") : t("common.registerAndAddToBasket")}
          </Button>
        )}

        {hasTagsOrCategories && (
          <>
            <Separator />
            <div className="space-y-3">
              {categoryNames.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    {t("course.categories")}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {categoryNames.map((name) => (
                      <span
                        key={name}
                        className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded-full font-medium"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {tagNames.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5">
                    {t("course.tags")}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {tagNames.map((name) => (
                      <span
                        key={name}
                        className="text-xs border border-foreground/30 text-foreground px-2 py-0.5 rounded-full"
                      >
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <Separator />

        {/* Course Stats */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>{t("course.enrolledStudents")}:</span>
          <span className="text-foreground font-medium">{enrolledStudents}</span>
        </div>
      </div>
    </div>
  );
};
export default CourseSidebar;
